// Arbeitszeitenprüfung — ArbZG-Compliance-Verstöße im gewählten Zeitraum.
// Wiederverwendbarer Body: wird in /time-accounts als Tab eingebunden.
// Standalone-Page-Wrapper bewusst nicht mehr da — die einzige Heimat ist
// jetzt der Tab in Stundenkonten.
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Calendar, Clock, ExternalLink, Moon } from 'lucide-react'
import { useState } from 'react'
import { api, type ComplianceViolation } from '../lib/api'
import { useDataTable } from '../lib/data-table'
import { cn } from '../lib/utils'
import { Pager, SortableHeader, TableToolbar } from './data-table'
import { Stat } from './range-report-view'
import { Button } from './ui/button'
import { Select } from './ui/select'

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}
function firstOfMonthIso(): string {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().slice(0, 10)
}

const VIOLATION_CONFIG: Record<
  ComplianceViolation['type'],
  {
    label: string
    colorClass: string
    bgClass: string
    borderClass: string
    Icon: typeof AlertTriangle
  }
> = {
  max_hours: {
    label: 'Höchstarbeitszeit',
    colorClass: 'text-danger',
    bgClass: 'bg-danger/15',
    borderClass: 'border-danger/40',
    Icon: AlertTriangle,
  },
  missing_break: {
    label: 'Pausenverstoß',
    colorClass: 'text-danger',
    bgClass: 'bg-danger/15',
    borderClass: 'border-danger/40',
    Icon: Clock,
  },
  rest_period: {
    label: 'Ruhezeit',
    colorClass: 'text-warning',
    bgClass: 'bg-warning/15',
    borderClass: 'border-warning/40',
    Icon: Moon,
  },
  sunday_work: {
    label: 'Sonntagsarbeit',
    colorClass: 'text-warning',
    bgClass: 'bg-warning/15',
    borderClass: 'border-warning/40',
    Icon: Calendar,
  },
}

const SEVERITY_CONFIG: Record<
  ComplianceViolation['severity'],
  { label: string; colorClass: string; bgClass: string; borderClass: string }
> = {
  error: {
    label: 'Fehler',
    colorClass: 'text-danger',
    bgClass: 'bg-danger/15',
    borderClass: 'border-danger/40',
  },
  warning: {
    label: 'Warnung',
    colorClass: 'text-warning',
    bgClass: 'bg-warning/15',
    borderClass: 'border-warning/40',
  },
}

export function ComplianceView() {
  const [from, setFrom] = useState(firstOfMonthIso())
  const [to, setTo] = useState(todayIso())
  const [userId, setUserId] = useState<string>('')
  const navigate = useNavigate()

  const { data: usersData } = useQuery({
    queryKey: ['users', 'manageable'],
    queryFn: () => api.listManageableUsers(),
  })

  const { data: complianceData, isLoading } = useQuery({
    queryKey: ['reports', 'compliance', { from, to, userId }],
    queryFn: () => api.complianceReport({ from, to, userId: userId || undefined }),
  })

  const violations = complianceData?.violations ?? []
  const manageableUsers = usersData?.users ?? []
  const showUserFilter = manageableUsers.length > 1

  const totalViolations = violations.length
  const errorCount = violations.filter((v) => v.severity === 'error').length
  const warningCount = violations.filter((v) => v.severity === 'warning').length

  const table = useDataTable<ComplianceViolation>(
    violations,
    {
      date: (row) => row.date,
      userName: (row) => row.userName,
      type: (row) => VIOLATION_CONFIG[row.type].label,
      severity: (row) => (row.severity === 'error' ? 0 : 1),
    },
    {
      searchableFields: [
        (row) => row.userName,
        (row) => row.description,
        (row) => VIOLATION_CONFIG[row.type].label,
      ],
      defaultSortKey: 'date',
      defaultSortDir: 'desc',
      defaultPageSize: 25,
      storageKey: 'compliance-violations',
      rowId: (row) => row.id,
    },
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label
            htmlFor="comp-from"
            className="block text-xs uppercase tracking-wider text-text-tertiary"
          >
            von
          </label>
          <input
            id="comp-from"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-10 rounded-lg border border-border-default bg-bg-overlay/50 px-3 text-sm text-text-primary [color-scheme:dark] focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20"
          />
        </div>
        <div className="space-y-1">
          <label
            htmlFor="comp-to"
            className="block text-xs uppercase tracking-wider text-text-tertiary"
          >
            bis
          </label>
          <input
            id="comp-to"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-10 rounded-lg border border-border-default bg-bg-overlay/50 px-3 text-sm text-text-primary [color-scheme:dark] focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20"
          />
        </div>
        {showUserFilter && (
          <div className="space-y-1 flex-1 min-w-[200px] max-w-xs">
            <label
              htmlFor="comp-user"
              className="block text-xs uppercase tracking-wider text-text-tertiary"
            >
              Mitarbeiter
            </label>
            <Select id="comp-user" value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {manageableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Verstöße gesamt" value={String(totalViolations)} />
        <Stat label="davon Fehler" value={String(errorCount)} accent={errorCount > 0} danger />
        <Stat
          label="davon Warnungen"
          value={String(warningCount)}
          accent={warningCount > 0}
          warning
        />
      </div>

      {isLoading ? (
        <p className="text-text-secondary">Lade …</p>
      ) : violations.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border-default bg-bg-elevated/30 px-6 py-16 text-center">
          <p className="text-lg text-text-secondary">Keine Verstöße im gewählten Zeitraum 🎉</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
          <TableToolbar
            search={table.search}
            onSearch={table.setSearch}
            filteredRows={table.filteredRows}
            totalRows={table.totalRows}
            pageSize={table.pageSize}
            onPageSize={table.setPageSize}
          />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border-subtle bg-bg-elevated/60">
                  <th className="px-4 py-2 text-left">
                    <SortableHeader
                      label="Datum"
                      sortKey="date"
                      currentKey={table.sortKey}
                      currentDir={table.sortDir}
                      onSort={table.toggleSort}
                    />
                  </th>
                  {showUserFilter && (
                    <th className="px-4 py-2 text-left">
                      <SortableHeader
                        label="Mitarbeiter"
                        sortKey="userName"
                        currentKey={table.sortKey}
                        currentDir={table.sortDir}
                        onSort={table.toggleSort}
                      />
                    </th>
                  )}
                  <th className="px-4 py-2 text-left">
                    <SortableHeader
                      label="Typ"
                      sortKey="type"
                      currentKey={table.sortKey}
                      currentDir={table.sortDir}
                      onSort={table.toggleSort}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">
                    <SortableHeader
                      label="Schwere"
                      sortKey="severity"
                      currentKey={table.sortKey}
                      currentDir={table.sortDir}
                      onSort={table.toggleSort}
                    />
                  </th>
                  <th className="px-4 py-2 text-left">
                    <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                      Beschreibung
                    </span>
                  </th>
                  <th className="px-4 py-2 text-right">
                    <span className="text-[11px] uppercase tracking-wider font-medium text-text-tertiary">
                      Aktion
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((v) => {
                  const typeCfg = VIOLATION_CONFIG[v.type]
                  const sevCfg = SEVERITY_CONFIG[v.severity]
                  const Icon = typeCfg.Icon
                  return (
                    <tr
                      key={v.id}
                      className="border-b border-border-subtle last:border-b-0 hover:bg-bg-overlay/30 transition-colors cursor-pointer"
                      onClick={() => navigate({ to: '/calendar', search: { date: v.date } })}
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs tabular-nums text-text-primary">
                        {formatDateDE(v.date)}
                      </td>
                      {showUserFilter && (
                        <td className="px-4 py-3 text-text-primary text-sm">{v.userName}</td>
                      )}
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-2xs font-medium border',
                            typeCfg.bgClass,
                            typeCfg.borderClass,
                            typeCfg.colorClass,
                          )}
                        >
                          <Icon className="h-3 w-3" />
                          {typeCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2 py-0.5 text-2xs font-medium border',
                            sevCfg.bgClass,
                            sevCfg.borderClass,
                            sevCfg.colorClass,
                          )}
                        >
                          {sevCfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-text-secondary text-sm max-w-xs truncate">
                        {v.description}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate({ to: '/calendar', search: { date: v.date } })
                          }}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline ml-1">Zum Eintrag</span>
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <Pager
            page={table.page}
            totalPages={table.totalPages}
            onPage={table.setPage}
            filteredRows={table.filteredRows}
            pageSize={table.pageSize}
          />
        </div>
      )}
    </div>
  )
}

function formatDateDE(iso: string): string {
  const [y, m, d] = iso.split('-')
  return `${d}.${m}.${y}`
}
